import { queryOptions } from "@tanstack/react-query";

import {
  getMyCourse,
  getMyCourses,
  getMyCourseWeek,
  getMyHelpVideos,
  getMyPlacementVideos,
  getPublicPlacementVideo,
} from "~/lib/functions/learning";
import type { PublicVideoPlacementKey } from "~/lib/shared/learning/placements";

/**
 * Keys carry no user id, like the feature-flag queries: the server answers for
 * the signed-in coach, and a sign-in change reloads the page.
 */
export const learningKeys = {
  placementVideos: ["learning", "placement-videos"] as const,
  helpVideos: ["learning", "help-videos"] as const,
  publicPlacementVideo: (placement: PublicVideoPlacementKey) =>
    ["learning", "public-placement-video", placement] as const,
  courses: ["learning", "courses"] as const,
  course: (slug: string) => ["learning", "course", slug] as const,
  week: (slug: string, number: number) =>
    ["learning", "course", slug, "week", number] as const,
};

export function placementVideosQueryOptions() {
  return queryOptions({
    queryKey: learningKeys.placementVideos,
    queryFn: () => getMyPlacementVideos(),
    // Videos change when an admin assigns one, and a coach's audience changes
    // with her plan; a minute keeps both current without a fetch per page.
    staleTime: 60 * 1000,
  });
}

/** The help videos for the viewer's role; the Help page and every "How this works" button read it. */
export function helpVideosQueryOptions() {
  return queryOptions({
    queryKey: learningKeys.helpVideos,
    queryFn: () => getMyHelpVideos(),
    staleTime: 60 * 1000,
  });
}

/** The same for every visitor, so it caches like the coach placements. */
export function publicPlacementVideoQueryOptions(
  placement: PublicVideoPlacementKey,
) {
  return queryOptions({
    queryKey: learningKeys.publicPlacementVideo(placement),
    queryFn: () => getPublicPlacementVideo({ data: { placement } }),
    staleTime: 60 * 1000,
  });
}

export function myCoursesQueryOptions() {
  return queryOptions({
    queryKey: learningKeys.courses,
    queryFn: () => getMyCourses(),
    staleTime: 5 * 60 * 1000,
  });
}

export function myCourseQueryOptions(slug: string) {
  return queryOptions({
    queryKey: learningKeys.course(slug),
    queryFn: () => getMyCourse({ data: { slug } }),
  });
}

export function myCourseWeekQueryOptions(slug: string, number: number) {
  return queryOptions({
    queryKey: learningKeys.week(slug, number),
    queryFn: () => getMyCourseWeek({ data: { slug, number } }),
  });
}
